import { AppProps } from '../../@types/AppProps';
import { EvCalculationChargingPerKilometer } from '../../@types/EvCalculation';
import ErrorAlert from '../../Components/ErrorAlert';
import HtmlNode from '../../Components/Extension/HtmlNode';
import InputField from '../../Components/Input/InputField';
import InputRadioGroup from '../../Components/Input/InputRadioGroup';
import getCostPerKilometer from '../../Http/getCostPerKilometer';
import { RequestError } from '../../Http/http';
import { EvEvent, EvView, pushEvent } from '../../Utils/events';
import { onlyFloat } from '../../Utils/format';
import ModalResultStats from './ModalResultStats';
import { MarketCode } from '../../@types/MarketCode';
import i18next from '../../Utils/i18n';

const PRICE_OPTION = 'wayke-ev-extended-price-option';
const PRICE_NODE = `${PRICE_OPTION}-node`;

const CUSTOM_PRICE_INPUT = 'wayke-ev-extended-price-input-custom-node';
const CUSTOM_PRICE_INPUT_NODE = `${CUSTOM_PRICE_INPUT}-node`;
const REQUEST_ERROR = 'wayke-ev-req-error';

const STATS_NODE = 'wayke-ev-extended-stats-node';

const STANDARD_PRICE = 'standardPrice';
const CUSTOM_PRICE = 'customPrice';

type PriceOptionTypes = 'standardPrice' | 'customPrice';

const RadioElements = () => [
  {
    id: `radio-standard-price`,
    value: STANDARD_PRICE,
    title: i18next.t('standardPrice') || undefined,
  },
  {
    id: `radio-custom-price`,
    value: CUSTOM_PRICE,
    title: i18next.t('statedPrice') || undefined,
  },
];

interface ModalResultProps {
  settings: AppProps;
  charginCostPerKilometer: EvCalculationChargingPerKilometer;
  distance: number;
  marketCode: MarketCode;
}

interface ModalResultContext {
  input?: InputField;
  priceOption?: InputRadioGroup;
  stats?: ModalResultStats;
  errorAlert?: ErrorAlert;
}

let storedCostPerKwh: number | undefined;
let timeoutRef: number = -1;
class ModalResult extends HtmlNode {
  private readonly props: ModalResultProps;
  private contexts: ModalResultContext = {};
  private priceOption: PriceOptionTypes = STANDARD_PRICE;
  private costPerKwh?: number;
  private charginCostPerKilometer: EvCalculationChargingPerKilometer;
  private distance: number;
  private costPerKilometerCustom?: EvCalculationChargingPerKilometer;
  private marketCode: MarketCode;

  constructor(element: HTMLElement | null, props: ModalResultProps) {
    super(element);
    this.props = props;
    this.charginCostPerKilometer = props.charginCostPerKilometer;
    this.costPerKwh =
      storedCostPerKwh || (props.charginCostPerKilometer?.description.costPerKwh.value || 0) * 100;
    this.distance = props.distance;
    this.costPerKilometerCustom = props.charginCostPerKilometer;
    this.marketCode = props.marketCode;

    this.render();
  }

  private async fetchData() {
    if (!this.props.settings.id) return;
    this.contexts.errorAlert?.update(undefined);
    try {
      pushEvent(EvView.MODAL, EvEvent.REQUEST_CUSTOM_PRICE_PENDING, { id: this.props.settings.id });
      const response = await getCostPerKilometer(
        this.props.settings.url,
        this.props.settings.id,
        (this.costPerKwh || 0) / 100
      );

      pushEvent(EvView.MODAL, EvEvent.REQUEST_CUSTOM_PRICE_SUCCEEDED, {
        id: this.props.settings.id,
      });
      this.costPerKilometerCustom = response;
      this.contexts.stats?.update(this.costPerKilometerCustom, this.distance);
    } catch (e) {
      const statusCode = (e as RequestError)?.status;
      pushEvent(EvView.MODAL, EvEvent.REQUEST_CUSTOM_PRICE_FAILED, {
        id: this.props.settings.id,
        statusCode,
      });
      this.contexts.errorAlert?.update(
        `${i18next.t('anErrorHasOccured')} <button type="button" title="${i18next.t(
          'tryAgain'
        )}" class="waykeev-link" id="waykeev-error-retry">
          ${i18next.t('tryAgain')}
        </button>`
      );
      this.contexts.errorAlert?.node
        ?.querySelector('#waykeev-error-retry')
        ?.addEventListener('click', () => this.fetchData());
      this.contexts.stats?.hide();
    }
  }

  private onChangeRadio(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const value = currentTarget.value as PriceOptionTypes;
    this.priceOption = value;
    pushEvent(EvView.MODAL, EvEvent.TOGGLE_PRICE, {
      id: this.props.settings.id,
      priceOption: value,
    });
    this.render();
    if (
      this.priceOption === 'customPrice' &&
      this.costPerKwh !== this.props.charginCostPerKilometer?.description.costPerKwh.value
    ) {
      this.fetchData();
    } else if (this.costPerKilometerCustom && this.distance) {
      this.contexts.stats?.update(
        this.priceOption === 'customPrice'
          ? this.costPerKilometerCustom
          : this.props.charginCostPerKilometer,
        this.distance
      );
    }
  }

  update(distance: number) {
    this.distance = distance;
    this.contexts.stats?.update(
      this.priceOption === 'customPrice'
        ? this.costPerKilometerCustom
        : this.props.charginCostPerKilometer,
      this.distance
    );
  }

  private onChange(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const value = onlyFloat(currentTarget.value);
    currentTarget.value = value;
    this.costPerKwh =
      parseFloat(value) ||
      (this.props.charginCostPerKilometer?.description.costPerKwh.value || 0) * 100;
    clearTimeout(timeoutRef);
    timeoutRef = setTimeout(() => {
      if (this.costPerKwh) {
        pushEvent(EvView.MODAL, EvEvent.CUSTOM_PRICE_CHANGE, { customPrice: this.costPerKwh });
        storedCostPerKwh = this.costPerKwh;
        this.fetchData();
      }
    }, 250) as unknown as number;
  }

  render() {
    this.node.innerHTML = `
      <div class="waykeev-stack waykeev-stack--3">
        <div class="waykeev-stack waykeev-stack--2" id="${PRICE_NODE}"></div>
        <div class="waykeev-stack waykeev-stack--2" id="${CUSTOM_PRICE_INPUT_NODE}"></div>
        <div class="waykeev-stack waykeev-stack--2" id="${REQUEST_ERROR}"></div>
      </div>
      <div class="waykeev-stack waykeev-stack--3" id="${STATS_NODE}"></div>
    `;

    if (this.priceOption === 'customPrice') {
      this.contexts.input = new InputField(
        this.node.querySelector<HTMLDivElement>(`#${CUSTOM_PRICE_INPUT_NODE}`),
        {
          id: CUSTOM_PRICE_INPUT,
          title: i18next.t('yourElectricPrice') || '',
          name: 'custom-price',
          value: this.costPerKwh?.toString() || '',
          unit: i18next.t('costPerKwh') || undefined,
          information: i18next.t('stateYourTotalKwh') || undefined,
          pattern: '[0-9]*',
          inputmode: 'numeric',
          onChange: (e) => this.onChange(e),
        }
      );
    }

    this.contexts.priceOption = new InputRadioGroup(this.node.querySelector(`#${PRICE_NODE}`), {
      title: i18next.t('selectPriceToCalculate') || undefined,
      checked: this.priceOption,
      name: 'price',
      options: RadioElements(),
      onClick: (e) => this.onChangeRadio(e),
    });

    this.contexts.stats = new ModalResultStats(this.node.querySelector(`#${STATS_NODE}`), {
      priceOption: this.priceOption,
      charginCostPerKilometer:
        this.priceOption === 'standardPrice'
          ? this.charginCostPerKilometer
          : this.costPerKilometerCustom,
      distance: this.distance || 0,
      marketCode: this.marketCode,
    });

    this.contexts.errorAlert = new ErrorAlert(
      this.node.querySelector<HTMLDivElement>(`#${REQUEST_ERROR}`),
      {}
    );
  }
}

export default ModalResult;
